import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Loader2, ExternalLink } from "lucide-react";
import { crmApi } from "@/lib/crm";

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  cover_image_url: "",
  hero_image_url: "",
  author_name: "Velor Advisory",
  published_at: "",
  content_markdown: "",
  category: "",
  status: "draft",
  seo_title: "",
  seo_description: "",
  focus_keyword: "",
};

const inputCls = "w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition";
const labelCls = "text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5 block";

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function InsightEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [data, setData] = useState({ ...empty });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    crmApi.getInsight(id)
      .then((d) => {
        setData({
          ...empty,
          ...d,
          published_at: d.published_at ? d.published_at.slice(0, 10) : "",
        });
        setSlugTouched(true);
      })
      .catch(() => toast.error("Failed to load article"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setData((d) => {
      const next = { ...d, [k]: v };
      if (k === "title" && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const save = async ({ publish = false } = {}) => {
    if (!data.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...data,
        status: publish ? "published" : data.status,
        published_at: data.published_at ? new Date(data.published_at).toISOString() : null,
      };
      const res = isNew
        ? await crmApi.createInsight(payload)
        : await crmApi.updateInsight(id, payload);
      toast.success(isNew ? "Article created" : "Article saved");
      if (isNew) navigate(`/crm/insights/${res.id}`, { replace: true });
      else setData({ ...empty, ...res, published_at: res.published_at ? res.published_at.slice(0, 10) : "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await crmApi.deleteInsight(id);
      toast.success("Deleted");
      navigate("/crm/insights", { replace: true });
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="insight-editor">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/crm/insights" className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-neutral-500 hover:text-neutral-900 dark:hover:text-white">
            <ArrowLeft size={12}/> All articles
          </Link>
          <h1 className="font-serif text-3xl mt-2">{isNew ? "New Article" : "Edit Article"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && data.status === "published" && (
            <a
              href={`/insights/${data.slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid="view-live-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-700 text-[11px] tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-900"
            >
              <ExternalLink size={12}/> View live
            </a>
          )}
          <button
            onClick={() => setPreview((p) => !p)}
            data-testid="preview-toggle"
            className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-700 text-[11px] tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-900"
          >
            {preview ? "Edit" : "Preview"}
          </button>
          {!isNew && (
            <button
              onClick={remove}
              data-testid="delete-article-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-rose-300 text-rose-700 text-[11px] tracking-overline uppercase hover:bg-rose-50"
            >
              <Trash2 size={12}/> Delete
            </button>
          )}
          <button
            onClick={() => save({ publish: false })}
            disabled={saving}
            data-testid="save-draft-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-700 text-[11px] tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-900 disabled:opacity-60"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save draft
          </button>
          <button
            onClick={() => save({ publish: true })}
            disabled={saving}
            data-testid="publish-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase disabled:opacity-60"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : null} Publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-8 space-y-4">
          <div>
            <label className={labelCls}>Title *</label>
            <input data-testid="editor-title" className={`${inputCls} !text-lg`} value={data.title} onChange={set("title")} placeholder="Give it a clear, considered headline" />
          </div>

          <div>
            <label className={labelCls}>Excerpt (shown on the insights list)</label>
            <textarea data-testid="editor-excerpt" className={`${inputCls} min-h-[70px]`} value={data.excerpt} onChange={set("excerpt")} maxLength={200} placeholder="One or two sentences that describe the briefing." />
            <div className="text-[10px] text-neutral-400 mt-1">{data.excerpt.length}/200</div>
          </div>

          <div>
            <label className={labelCls}>Content (markdown)</label>
            {preview ? (
              <div className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-900 min-h-[500px]">
                <div className="article-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content_markdown || "*Nothing to preview yet.*"}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                data-testid="editor-content"
                className={`${inputCls} min-h-[500px] font-mono text-[13px] leading-6`}
                value={data.content_markdown}
                onChange={set("content_markdown")}
                placeholder={"# H1 heading\n\nStart writing. Markdown supported — **bold**, *italic*, [links](https://velor.ae), lists, > blockquotes, and tables via GFM."}
              />
            )}
            <div className="text-[10px] text-neutral-400 mt-1">
              Markdown supported (GFM): headings, bold, italic, links, lists, blockquotes, tables, code.
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="border border-neutral-200 dark:border-neutral-800 p-4 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Publish</div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="editor-status" className={inputCls} value={data.status} onChange={set("status")}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Publish date</label>
              <input data-testid="editor-date" type="date" className={inputCls} value={data.published_at || ""} onChange={set("published_at")} />
            </div>
          </div>

          <div className="border border-neutral-200 dark:border-neutral-800 p-4 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Article details</div>
            <div>
              <label className={labelCls}>Author name</label>
              <input data-testid="editor-author" className={inputCls} value={data.author_name} onChange={set("author_name")} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <input data-testid="editor-category" className={inputCls} value={data.category} onChange={set("category")} placeholder="e.g. Golden Visa" list="insight-categories" />
              <datalist id="insight-categories">
                <option value="Golden Visa" />
                <option value="Business Setup" />
                <option value="Relocation" />
                <option value="Tax" />
                <option value="Property" />
                <option value="Banking" />
                <option value="Family & Schools" />
              </datalist>
            </div>
            <div>
              <label className={labelCls}>Cover image URL <span className="text-neutral-400 normal-case">(insights list card)</span></label>
              <input data-testid="editor-cover" className={inputCls} value={data.cover_image_url} onChange={set("cover_image_url")} placeholder="https://…" />
              {data.cover_image_url && (
                <div className="mt-2 aspect-[16/9] overflow-hidden border border-neutral-200 dark:border-neutral-800">
                  <img src={data.cover_image_url} alt="Cover preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Hero image URL <span className="text-neutral-400 normal-case">(shown inside the article)</span></label>
              <input data-testid="editor-hero" className={inputCls} value={data.hero_image_url} onChange={set("hero_image_url")} placeholder="Leave blank to hide" />
              {data.hero_image_url && (
                <div className="mt-2 aspect-[16/9] overflow-hidden border border-neutral-200 dark:border-neutral-800">
                  <img src={data.hero_image_url} alt="Hero preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>URL slug</label>
              <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1.5">/insights/</div>
              <input
                data-testid="editor-slug"
                className={inputCls}
                value={data.slug}
                onChange={(e) => { setSlugTouched(true); set("slug")(e); }}
                placeholder="auto-generated-from-title"
              />
            </div>
          </div>

          <div className="border border-neutral-200 dark:border-neutral-800 p-4 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">SEO</div>
            <div>
              <label className={labelCls}>SEO title (60 char max)</label>
              <input data-testid="editor-seo-title" className={inputCls} value={data.seo_title} onChange={set("seo_title")} maxLength={60} placeholder="Defaults to article title" />
              <div className="text-[10px] text-neutral-400 mt-1">{(data.seo_title || "").length}/60</div>
            </div>
            <div>
              <label className={labelCls}>Meta description (155 char max)</label>
              <textarea data-testid="editor-seo-desc" className={`${inputCls} min-h-[70px]`} value={data.seo_description} onChange={set("seo_description")} maxLength={155} placeholder="Defaults to the excerpt" />
              <div className="text-[10px] text-neutral-400 mt-1">{(data.seo_description || "").length}/155</div>
            </div>
            <div>
              <label className={labelCls}>Focus keyword</label>
              <input data-testid="editor-focus-kw" className={inputCls} value={data.focus_keyword} onChange={set("focus_keyword")} placeholder="e.g. UAE Golden Visa eligibility" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
